/**
 * The replays of the current / last match, app-wide (one instance, created at boot). The match scene hands it each
 * match's recorder; it publishes the clip list to `ui.replays` (Results reel cards) and implements the UI's
 * `ReplayService` (the reel's theater). Holds at most one match's clips (the recorder's bounded set).
 */
import type { MatchConfig } from '@hf/shared';
import type { AudioBridge } from '../audio-bridge/types';
import type { CreateMatchRenderer, RenderHost } from '@hf/render-core/types';
import type { ReplayService, ReplayTheaterHandle, ReplayTheaterState } from '../ui-model/actions';
import type { UiStore } from '../ui-model/store';
import type { ReplayClipInfo } from '../ui-model/types';
import type { AssetManifest } from '@hf/render-core/assets/manifest';
import type { FixedStepOptions } from '../loop';
import { renderOptionsFrom } from '../match/render-options';
import type { ReplayClip, ReplayRecorder } from './recorder';
import type { ReplayTheater } from './theater';

export const clipInfo = (c: ReplayClip): ReplayClipInfo => ({
  id: c.id,
  title: c.title,
  sub: c.sub,
  tone: c.tone,
  character: c.character,
  side: c.side,
  expression: c.expression,
  matchSec: c.matchSec,
  score: c.score,
});

/** Reel tapes shown on the Results screen. */
const REEL_VARIETY = 3;

/**
 * Best first, but the first REEL_VARIETY tapes prefer different kinds (a touchdown, a pick, a fake…) over three
 * sacks; the rest follow by score.
 */
export const reelOrder = (ranked: readonly ReplayClip[]): ReplayClip[] => {
  const head: ReplayClip[] = [];
  const kinds = new Set<string>();
  for (const c of ranked) {
    if (head.length >= REEL_VARIETY) break;
    if (!kinds.has(c.kind)) {
      kinds.add(c.kind);
      head.push(c);
    }
  }
  for (const c of ranked) if (head.length < REEL_VARIETY && !head.includes(c)) head.push(c);
  head.sort((a, b) => b.score - a.score || b.keyTick - a.keyTick);
  return [...head, ...ranked.filter((c) => !head.includes(c))];
};

export interface ReplayLibraryDeps {
  ui: UiStore;
  manifest: AssetManifest;
  createRenderer: CreateMatchRenderer;
  /** The reel's sound (see ReplayTheater). */
  audio?: Pick<AudioBridge, 'onTheater' | 'onTheaterEvents'>;
  frames?: Pick<FixedStepOptions, 'now' | 'requestFrame' | 'cancelFrame'>;
}

export class ReplayLibrary implements ReplayService {
  private recorder: ReplayRecorder | null = null;
  private unsub: (() => void) | null = null;
  private handle: ReplayTheaterHandle | null = null;

  constructor(private readonly deps: ReplayLibraryDeps) {}

  get config(): MatchConfig | null {
    return this.recorder?.config ?? null;
  }

  /** Clips of the current / last match, best first (the reel's first tapes are different kinds of plays). */
  clips(): ReplayClip[] {
    return reelOrder(this.recorder?.ranked() ?? []);
  }

  clip(id: number): ReplayClip | null {
    return this.recorder?.clips.find((c) => c.id === id) ?? null;
  }

  /** A new match records into `recorder` (null: this match records nothing, e.g. practice). */
  attach(recorder: ReplayRecorder | null): void {
    this.closeTheater();
    this.unsub?.();
    this.unsub = null;
    this.recorder = recorder;
    this.publish();
    if (recorder) this.unsub = recorder.onClip(() => this.publish());
  }

  private publish(): void {
    this.deps.ui.replays.value = this.clips().map(clipInfo);
  }

  open(
    el: RenderHost,
    ids: readonly number[],
    start: number,
    events: { onClip?(index: number): void; onState?(state: ReplayTheaterState): void },
  ): ReplayTheaterHandle {
    this.closeTheater();
    const clips = ids.map((id) => this.clip(id)).filter((c): c is ReplayClip => c !== null);
    const config = this.config;
    if (!config || clips.length === 0) {
      events.onState?.('error');
      return { play: () => {}, close: () => {} };
    }
    // The theater (and the sim it resimulates with) lives in the match chunk: load it on first use.
    let theater: ReplayTheater | null = null;
    let closed = false;
    let pendingPlay: number | null = null;
    events.onState?.('loading');
    const handle: ReplayTheaterHandle = {
      play: (i) => {
        if (theater) theater.play(i);
        else pendingPlay = i;
      },
      close: () => {
        closed = true;
        theater?.close();
        if (this.handle === handle) this.handle = null;
      },
    };
    this.handle = handle;
    import('./theater').then(
      ({ ReplayTheater }) => {
        if (closed) return;
        theater = new ReplayTheater(
          {
            createRenderer: this.deps.createRenderer,
            manifest: this.deps.manifest,
            options: { ...renderOptionsFrom(this.deps.ui.settings.peek()), debug: false },
            ...(this.deps.frames ? { frames: this.deps.frames } : {}),
            ...(this.deps.audio ? { audio: this.deps.audio } : {}),
          },
          config,
          clips,
          el,
          start,
          events,
        );
        if (pendingPlay !== null) theater.play(pendingPlay);
      },
      (err: unknown) => {
        console.error('[replay] theater failed to load', err);
        if (!closed) events.onState?.('error');
      },
    );
    return handle;
  }

  closeTheater(): void {
    this.handle?.close();
    this.handle = null;
  }
}
